/**
 * Pure search/filter/sort logic for AdvancedList — kept free of React state so
 * it is trivially unit-testable, same shape as apps/hub's routines-filter.ts.
 */

export interface AdvancedListFilterDef<T> {
  /** Stable id for this filter, used as the key in the active-filters map. */
  id: string;
  /** Label shown above the filter's option chips. */
  label: string;
  /** The options a user can toggle on for this filter. */
  options: Array<{ value: string; label: string }>;
  /** Pull the value(s) an item has for this filter — a single value or several. */
  getValue: (item: T) => string | string[] | null | undefined;
}

export interface AdvancedListSortDef<T> {
  /** Stable id for this sort, used as the active-sort key. */
  id: string;
  /** Label shown in the sort control. */
  label: string;
  /** Ascending comparator — the query flips it for descending. */
  compare: (a: T, b: T) => number;
}

export interface AdvancedListQueryState {
  /** Free-text search query. Empty string = no search filtering. */
  search: string;
  /** filterId -> set of selected option values. Absent/empty = that filter is off. */
  activeFilters: Record<string, string[]>;
  /** Active sort id, or null for the caller's incoming item order. */
  sortId: string | null;
  /** True = descending. */
  sortDescending: boolean;
}

export function defaultAdvancedListQueryState(): AdvancedListQueryState {
  return { search: "", activeFilters: {}, sortId: null, sortDescending: false };
}

function normalizeToArray(value: string | string[] | null | undefined): string[] {
  if (value == null) return [];
  return Array.isArray(value) ? value : [value];
}

/**
 * Runs search, then every active filter (AND across filters, OR within a
 * filter's own selected options), then the active sort — in that order.
 */
export function applyAdvancedListQuery<T>(
  items: readonly T[],
  state: AdvancedListQueryState,
  options: {
    /** Strings pulled from an item to match the free-text search against. */
    searchFields?: (item: T) => Array<string | null | undefined>;
    filters?: Array<AdvancedListFilterDef<T>>;
    sorts?: Array<AdvancedListSortDef<T>>;
  }
): T[] {
  let result = [...items];

  const query = state.search.trim().toLowerCase();
  if (query.length > 0 && options.searchFields) {
    result = result.filter((item) =>
      options
        .searchFields!(item)
        .some((field) => typeof field === "string" && field.toLowerCase().includes(query))
    );
  }

  if (options.filters) {
    for (const filterDef of options.filters) {
      const selected = state.activeFilters[filterDef.id];
      if (!selected || selected.length === 0) continue;
      const selectedSet = new Set(selected);
      result = result.filter((item) =>
        normalizeToArray(filterDef.getValue(item)).some((v) => selectedSet.has(v))
      );
    }
  }

  if (state.sortId && options.sorts) {
    const sortDef = options.sorts.find((s) => s.id === state.sortId);
    if (sortDef) {
      result = [...result].sort((a, b) =>
        state.sortDescending ? sortDef.compare(b, a) : sortDef.compare(a, b)
      );
    }
  }

  return result;
}

/**
 * Groups already-queried items by a key extractor, preserving each group's
 * internal order from `items`. Used for the optional grouped-section view —
 * intentionally simple (static grouping by key), not a multi-level pivot.
 */
export function groupAdvancedListItems<T>(
  items: readonly T[],
  groupBy: (item: T) => string
): Array<{ key: string; items: T[] }> {
  const order: string[] = [];
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const key = groupBy(item);
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key)!.push(item);
  }
  return order.map((key) => ({ key, items: groups.get(key)! }));
}
